import { useQuery } from '@tanstack/react-query'

import {
  getPhysiologicalDiagnoses,
  GetPhysiologicalDiagnosesResponse,
} from '@/services/get-physiological-diagnoses'
import { ILayers } from '@/types/diagnosis'

type UseGetPhysiologicalDiagnosesResponse = ReturnType<
  typeof useGetPhysiologicalDiagnoses
>['data']
export type DiagnosisDTO =
  NonNullable<UseGetPhysiologicalDiagnosesResponse>[number]

interface UseGetPhysiologicalDiagnosesOptions {
  cicle?: string
  layer?: ILayers
  projectId?: string
}

type UseGetPhysiologicalDiagnosesQueryOptions = {
  onError?: () => void
  enabled?: boolean
  idForFilter?: string
}

export const GET_DIAGNOSES_QUERY = 'diagnoses'

function cicleFormatted(cicle: string) {
  return cicle
    .split('/')
    .map((date) => date.substring(2))
    .join('/')
}

function dateFormatted(date: string) {
  return new Date(date).toLocaleDateString('pt-BR', {
    timeZone: 'UTC',
  })
}

function transformResponse(
  data: GetPhysiologicalDiagnosesResponse,
  idForFilter?: string,
) {
  return data
    .filter((diagnosis) => (idForFilter ? diagnosis.id !== idForFilter : true))
    .map((diagnosis) => {
      return {
        ...diagnosis,
        formattedCicle: diagnosis.cicle ? cicleFormatted(diagnosis.cicle) : '',
        formattedDate: dateFormatted(diagnosis.mosaic.date),
      }
    })
}

export const useGetPhysiologicalDiagnoses = (
  options?: UseGetPhysiologicalDiagnosesOptions,
  queryOptions?: UseGetPhysiologicalDiagnosesQueryOptions,
) => {
  const query = useQuery(
    [GET_DIAGNOSES_QUERY, options],
    ({ signal }) =>
      getPhysiologicalDiagnoses(
        {
          projectId: options?.projectId,
          cicle: options?.cicle,
          layer: options?.layer,
        },
        { signal },
      ),
    {
      select: (data) => transformResponse(data, queryOptions?.idForFilter),
      ...queryOptions,
    },
  )

  return query
}
